import React, { useState, useEffect } from 'react';
import { useToast } from '../../contexts/ToastContext';

interface ShareDraftModalProps {
  show: boolean;
  onClose: () => void;
  shareLink: string;
  draftNumber?: string;
}

const ShareDraftModal: React.FC<ShareDraftModalProps> = ({ 
  show, 
  onClose, 
  shareLink, 
  draftNumber 
}) => {
  const { showSuccess } = useToast();
  const [qrEnlarged, setQrEnlarged] = useState(false);
  const [windowWidth, setWindowWidth] = useState(window.innerWidth);

  // Handle window resize for responsive QR code sizing
  useEffect(() => {
    const handleResize = () => {
      setWindowWidth(window.innerWidth);
    };

    window.addEventListener('resize', handleResize);
    return () => window.removeEventListener('resize', handleResize);
  }, []);

  // Auto-copy link to clipboard when modal opens
  useEffect(() => {
    if (show && shareLink) {
      navigator.clipboard.writeText(shareLink);
      showSuccess('Link copied to clipboard!');
    }
  }, [show, shareLink, showSuccess]);

  // Reset QR enlarged state when modal closes
  useEffect(() => {
    if (!show) {
      setQrEnlarged(false);
    }
  }, [show]);

  // Calculate responsive QR code size
  const getQrSize = () => {
    if (qrEnlarged) {
      return Math.min(450, windowWidth * 0.6);
    }
    return Math.min(300, windowWidth * 0.4);
  };

  if (!show) return null;

  return (
    <div className="modal fade show d-block" style={{backgroundColor: 'rgba(0,0,0,0.5)', fontSize: '16px'}}>
      <div className="modal-dialog modal-dialog-centered" style={{
        maxWidth: '500px',
        width: '90%',
        margin: '0 auto'
      }}>
        <div className="modal-content">
          <div className="modal-header">
            <h5 className="modal-title">
              <i className="fas fa-share me-2"></i>Share Draft
              {draftNumber && (
                <small className="text-muted ms-2">({draftNumber})</small>
              )}
            </h5>
            <button 
              type="button" 
              className="btn-close" 
              onClick={() => {
                onClose();
                setQrEnlarged(false);
              }}
            ></button>
          </div>
          <div className="modal-body" style={{ maxHeight: '70vh', overflowY: 'auto' }}>
            <p>Share this draft with others using the link below:</p>
            <div className="input-group mb-3">
              <input
                type="text"
                className="form-control"
                value={shareLink}
                readOnly
                style={{ fontSize: '14px' }}
              />
              <button
                className="btn btn-outline-secondary"
                type="button"
                onClick={() => {
                  navigator.clipboard.writeText(shareLink);
                  showSuccess('Link copied to clipboard!');
                }}
              >
                Copy
              </button>
            </div>

            {/* QR Code */}
            <div className="text-center mb-3">
              <p className="mb-2 text-muted">Or scan this QR code:</p>
              <div style={{ 
                position: 'relative', 
                display: 'inline-block',
                maxWidth: '100%'
              }}>
                <div style={{
                  border: '2px solid #dee2e6',
                  borderRadius: '8px',
                  padding: '10px',
                  background: 'white',
                  cursor: qrEnlarged ? 'default' : 'pointer',
                  position: 'relative',
                  maxWidth: '100%',
                  overflow: 'hidden'
                }}
                onClick={() => !qrEnlarged && setQrEnlarged(true)}
                >
                  <img
                    src={`/api/drafts/qr/${encodeURIComponent(shareLink)}?size=${getQrSize()}`}
                    alt="QR Code for share link"
                    style={{
                      display: 'block',
                      maxWidth: '100%',
                      width: `${getQrSize()}px`,
                      height: `${getQrSize()}px`
                    }}
                  />
                  {!qrEnlarged && (
                    <small className="text-muted d-block mt-2">
                      <i className="fas fa-search-plus me-1"></i>
                      Tap to enlarge
                    </small>
                  )}
                </div>
                {qrEnlarged && (
                  <button
                    className="btn btn-sm btn-secondary position-absolute"
                    style={{ top: '10px', right: '10px' }}
                    onClick={() => setQrEnlarged(false)}
                  >
                    Close
                  </button>
                )}
              </div>
            </div>

            <div className="alert alert-info" style={{ textAlign: 'left' }}>
              <i className="fas fa-info-circle me-2"></i>
              This link will expire in 7 days. The link has been automatically copied to your clipboard.
            </div>
            <div className="alert alert-info" style={{ textAlign: 'left' }}>
              <i className="fas fa-info-circle me-2"></i>
              Note: You can send Draft Orders to anyone in your organization for approval or payment.
            </div>
          </div>
          <div className="modal-footer">
            <button 
              type="button"
              className="btn btn-success"
              onClick={() => {
                onClose();
                setQrEnlarged(false);
              }}
            >
              Close
            </button>
          </div>
        </div>
      </div>
    </div>
  );
};

export default ShareDraftModal;
